// Pure state-and-URL logic for the Teams list, kept out of orgs-list.ts so it
// is unit-testable without a DOM (see orgs-list.test.ts). Nothing here may
// value-import another project module: node --test cannot resolve the
// project's extension-less imports.

import type { ListParams } from '../api/types';

export type OrgsSortBy = NonNullable<ListParams['sortBy']>;

export interface OrgsListState {
  search: string;
  sortBy: OrgsSortBy;
  order: 'asc' | 'desc';
  windowDays: number;
  page: number;
  renewal: ListParams['renewal'];
}

/** The list's state as the URL's query describes it, so a view is shareable and reload-safe. */
export function parseOrgsListState(query: URLSearchParams): OrgsListState {
  return {
    search: query.get('search') ?? '',
    sortBy: (query.get('sortBy') as OrgsSortBy) || 'createdAt',
    order: (query.get('order') as 'asc' | 'desc') || 'desc',
    windowDays: Number(query.get('windowDays')) || 30,
    page: Number(query.get('page')) || 1,
    renewal: query.get('renewal') === 'cancelling' ? 'cancelling' : undefined,
  };
}

/**
 * The URL that describes `state`, on the path the list is on (`/teams`, or
 * `/organizations`): only what differs from the defaults is written. Reloading
 * it parses back to the same state. The path must be the page's own: writing
 * `/` here would make a reload open the Overview page, not the filtered list.
 */
export function orgsListUrl(pathname: string, state: OrgsListState): string {
  const usp = new URLSearchParams();
  if (state.search) usp.set('search', state.search);
  if (state.sortBy !== 'createdAt') usp.set('sortBy', state.sortBy);
  if (state.order !== 'desc') usp.set('order', state.order);
  if (state.windowDays !== 30) usp.set('windowDays', String(state.windowDays));
  if (state.page !== 1) usp.set('page', String(state.page));
  if (state.renewal) usp.set('renewal', state.renewal);
  const qs = usp.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

/**
 * What pressing the Cancelling chip does: the filter flips, and the list goes
 * back to page 1 (a filtered list has fewer pages, so staying on page 4 would
 * show nothing). Returns a new state; the view assigns it over its own.
 */
export function toggleCancelling(state: OrgsListState): OrgsListState {
  return { ...state, renewal: state.renewal ? undefined : 'cancelling', page: 1 };
}

export const CANCELLING_FILTER_UNSUPPORTED = 'This API does not support the Cancelling filter yet.';

/**
 * True when the Cancelling filter is on but the API plainly did not apply it:
 * an API that predates `renewal` strips the unknown query parameter and lists
 * every team, and its items have no `renewal` key at all (a team with nothing
 * to renew has `renewal: null`, which is a key). The list then shows this
 * message instead of a table of all teams under a pressed Cancelling chip.
 */
export function cancellingFilterIgnored(
  renewal: OrgsListState['renewal'],
  items: readonly object[],
): boolean {
  return renewal === 'cancelling' && items.some((item) => !('renewal' in item));
}
